<template>
  <div class="create-post">
    <div class="blog-container">
      <div class="blog-header">
        <div class="title-section">
          <v-text-field
            v-model="blog.title"
            placeholder="Enter Blog Title"
            solo
            flat
            hide-details
            class="title-input"
            background-color="transparent"
            counter="300"
            maxlength="300"
          ></v-text-field>

          <div class="upload-file">
            <v-btn
              color="primary"
              outlined
              @click="$refs.blogPhoto.click()"
              class="upload-btn"
            >
              <v-icon left>mdi-image-plus</v-icon>
              Upload Cover Photo
            </v-btn>
            <input
              ref="blogPhoto"
              id="blog-photo"
              @change="fileChange"
              class="d-none"
              type="file"
              accept="image/*"
            />
          </div>
        </div>
      </div>

      <div class="cover-image-container">
        <v-img
          :src="selectedImage"
          v-if="selectedImage"
          class="cover-image"
          gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.5)"
        >
          <div class="cover-overlay">
            <v-btn
              @click="$refs.blogPhoto.click()"
              color="white"
              outlined
              class="change-cover-btn"
            >
              <v-icon left>mdi-image-edit</v-icon>
              Change Cover
            </v-btn>
          </div>
        </v-img>
        <div v-else class="empty-cover" @click="$refs.blogPhoto.click()">
          <v-icon x-large color="grey lighten-1">mdi-image-plus</v-icon>
          <p>Add a cover image</p>
        </div>
      </div>

      <div class="editor-container">
        <vue-editor
          :editorOptions="editorSettings"
          v-model="blog.html"
          useCustomImageHandler
          @image-added="imageHandler"
          class="rich-text-editor"
          placeholder="Write your blog content here..."
        />
      </div>

      <div class="blog-actions">
        <v-btn
          @click="uploadBlog"
          :loading="loading"
          color="primary"
          large
          depressed
          class="action-button"
        >
          <v-icon left>mdi-publish</v-icon>
          Publish Blog
        </v-btn>

        <v-btn
          @click="onBlogPreview"
          color="secondary"
          outlined
          large
          class="action-button"
        >
          <v-icon left>mdi-eye</v-icon>
          Post Preview
        </v-btn>

        <v-btn @click="cancel" color="grey" text large class="action-button">
          <v-icon left>mdi-close</v-icon>
          Cancel
        </v-btn>
      </div>

      <blog-preview
        :dialog="previewDialog"
        :onClose="onClosePreview"
        :blog="blog"
      />

      <v-snackbar v-model="error" color="error" bottom center>
        {{ errorMsg }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="error = false">
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </div>
  </div>
</template>

<script>
import Quill from "quill";
import { mapActions } from "vuex";
import BlogPreview from "./BlogPreview.vue";
import DOMPurify from "dompurify";

window.Quill = Quill;

// Use the updated image resize module that doesn't have deprecation warnings
try {
  const ImageResize = require("quill-image-resize").default;
  Quill.register("modules/imageResize", ImageResize);
} catch (e) {
  // Fallback to older module if new one not available
  console.warn("quill-image-resize not found, falling back to older module");
  try {
    const ImageResize = require("quill-image-resize-module").default;
    Quill.register("modules/imageResize", ImageResize);
  } catch (fallbackError) {
    console.warn("Image resize module not available");
  }
}

// Remove video resize to avoid deprecation warnings
// const VideoResize = require("quill-video-resize-module").default;
// Quill.register("modules/VideoResize", VideoResize);

export default {
  components: { BlogPreview },
  name: "CreatePost",
  data() {
    return {
      file: null,
      error: null,
      errorMsg: null,
      loading: null,
      selectedImage: null, // ✅ Add this missing property
      previewDialog: false, // ✅ Add this missing property
      blog: { title: "", html: "" },

      // Secure editor settings
      editorSettings: {
        modules: {
          imageResize: {},
          toolbar: [
            [{ header: [1, 2, 3, false] }], // Remove h4,h5,h6
            ["bold", "italic", "underline"], // Remove strike
            [{ color: [] }], // Remove background colors
            ["blockquote"], // Remove code-block
            [{ list: "ordered" }, { list: "bullet" }],
            ["link", "image"], // Remove video
            ["clean"]
          ]
        }
      },

      // Sanitization config
      sanitizeConfig: {
        ALLOWED_TAGS: [
          "p",
          "br",
          "strong",
          "em",
          "u",
          "h1",
          "h2",
          "h3",
          "ul",
          "ol",
          "li",
          "blockquote",
          "a",
          "img"
        ],
        ALLOWED_ATTR: ["href", "src", "alt", "title"],
        FORBID_TAGS: ["script", "object", "embed", "form", "input", "style"],
        FORBID_ATTR: [
          "onerror",
          "onload",
          "onclick",
          "onmouseover",
          "style",
          "javascript"
        ]
      }
    };
  },
  methods: {
    ...mapActions("blog", {
      uploadBlogImage: "uploadBlogImage",
      createBlog: "createBlog"
    }),
    fileChange() {
      if (this.$refs.blogPhoto.files.length != 0) {
        this.file = this.$refs.blogPhoto.files[0];
        this.selectedImage = URL.createObjectURL(this.file);
        this.blog.cover = this.selectedImage;
      }
    },
    onBlogPreview() {
      if (!this.blog.title || this.blog.title.trim() === "") {
        this.error = true;
        this.errorMsg = "Please enter a blog title";
        return;
      }

      if (!this.blog.html.trim()) {
        this.error = true;
        this.errorMsg = "Please write some content for your blog";
        return;
      }

      // ✅ SANITIZE BEFORE PREVIEW
      this.blog.html = this.sanitizeContent(this.blog.html);
      this.previewDialog = true;
    },
    onClosePreview() {
      this.previewDialog = false;
    },
    imageHandler(file, Editor, cursorLocation, resetUploader) {
      var formData = new FormData();
      formData.append("file", file);
      this.uploadBlogImage(formData)
        .then(res => {
          Editor.insertEmbed(cursorLocation, "image", res);
          resetUploader();
        })
        .catch(error => {
          this.error = true;
          this.errorMsg =
            error.response?.data.message || "Failed to upload image";
        });
    },
    sanitizeContent(content) {
      // Log original content (for testing only)
      console.log("ORIGINAL CONTENT:", content);
      
      const sanitized = DOMPurify.sanitize(content, this.sanitizeConfig);
      
      // Log sanitized content (for testing only)
      console.log("SANITIZED CONTENT:", sanitized);
      
      return sanitized;
    },
    uploadBlog() {
      if (!this.blog.title || this.blog.title.trim() === "") {
        this.error = true;
        this.errorMsg = "Please enter a blog title";
        return;
      }

      if (!this.blog.html.trim()) {
        this.error = true;
        this.errorMsg = "Please write some content for your blog";
        return;
      }

      if (!this.file) {
        this.error = true;
        this.errorMsg = "Please upload a cover photo";
        return;
      }

      // ✅ SANITIZE BEFORE SENDING
      const sanitizedHTML = this.sanitizeContent(this.blog.html);
      const sanitizedTitle = this.sanitizeContent(this.blog.title);

      var formData = new FormData();
      formData.append("html", sanitizedHTML); // Safe HTML
      formData.append("title", sanitizedTitle); // Safe title
      formData.append("cover", this.file);

      this.loading = true;
      this.createBlog(formData)
        .then(() => {
          this.$router.push({
            name: "blog-feeds"
          });
        })
        .catch(error => {
          this.error = true;
          this.errorMsg =
            error.response?.data.message || "Failed to create blog";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    cancel() {
      this.$router.push({ name: "blog-feeds" });
    }
  }
};
</script>

<style lang="scss" scoped>
.create-post {
  // background-color: #f5f7fa;
  min-height: 100vh;
  padding: 24px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  box-sizing: border-box;

  .blog-container {
    max-width: 1200px;
    margin: 0 auto;
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    padding: 32px;
    position: relative;
    width: 100%;
    box-sizing: border-box;
  }

  .blog-header {
    margin-bottom: 24px;

    .title-section {
      display: flex;
      align-items: center;
      gap: 16px;
      width: 100%;
      justify-content: space-between;

      .title-input {
        font-size: 2rem;
        font-weight: 700;
        flex-grow: 1;
        ::v-deep .v-input__slot {
          padding: 0 !important;
          min-height: auto !important;
        }
        ::v-deep input {
          font-size: 2rem;
          font-weight: 700;
        }
      }

      .upload-file {
        flex-shrink: 0;
      }
    }
  }

  .cover-image-container {
    margin-bottom: 32px;
    border-radius: 8px;
    overflow: hidden;
    position: relative;
    height: 400px;
    background-color: #f0f2f5;

    .cover-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .cover-overlay {
      position: absolute;
      bottom: 24px;
      right: 24px;
    }

    .empty-cover {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      color: #64748b;
      cursor: pointer;
      transition: background-color 0.3s;

      &:hover {
        background-color: #e2e8f0;
      }

      p {
        margin-top: 12px;
        font-size: 1.1rem;
      }
    }
  }

  .editor-container {
    margin-bottom: 32px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;

    .rich-text-editor {
      min-height: 500px;
      ::v-deep .ql-toolbar {
        border: none;
        border-bottom: 1px solid #e2e8f0;
      }
      ::v-deep .ql-container {
        border: none;
        font-size: 1.1rem;
        font-family: inherit;
      }
      ::v-deep .ql-editor {
        min-height: 500px;
        padding: 24px;
      }
      ::v-deep .ql-blank::before {
        font-style: normal;
        color: #94a3b8;
      }
    }
  }

  .blog-actions {
    display: flex;
    gap: 16px;
    justify-content: center;
    padding-top: 24px;
    border-top: 1px solid #e2e8f0;
    width: 100%;
  }

  .action-button {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 500;
  }

  .upload-btn {
    text-transform: none;
    letter-spacing: normal;
  }

  .change-cover-btn {
    text-transform: none;
    letter-spacing: normal;
  }
}

@media (max-width: 960px) {
  .create-post {
    padding: 16px;

    .blog-container {
      padding: 24px;
    }

    .blog-header {
      .title-section {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;

        .title-input {
          width: 100%;
          ::v-deep input {
            font-size: 1.5rem;
          }
        }
      }
    }

    .cover-image-container {
      height: 300px;
    }

    .editor-container {
      .rich-text-editor {
        min-height: 400px;
      }
    }

    .blog-actions {
      flex-direction: column;
      gap: 12px;

      .action-button {
        width: 100%;
      }
    }
  }
}

@media (max-width: 600px) {
  .create-post {
    .cover-image-container {
      height: 200px;
    }

    .editor-container {
      .rich-text-editor {
        min-height: 300px;
        ::v-deep .ql-editor {
          padding: 16px;
        }
      }
    }
  }
}
</style>
